import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import BotIcon from "../../../assets/images/chatfinal.gif";
import { Prompts } from "Samarth Module/components/SamarthAi/Prompts";
import SliderIcon from "../../../assets/images/sliderIcon.svg";
import NoteIcon from "../../../assets/images/notelIcon.svg";
import PromtsHistory from "Samarth Module/components/SamarthAi/PromtsHistory";
import PdfIcon from "../../../assets/images/pdfIcon.svg";
import BookmarkIcon from "../../../assets/images/bookmark.svg";
import LinesImg from "../../../assets/images/lines.svg";
import { MessageBubble } from "Samarth Module/components/SamarthAi/MessageBubble";
import { ChatInput } from "Samarth Module/components/SamarthAi/ChatInput";
import ActionMenu from "Samarth Module/components/SamarthAi/ActionMenu";
import {
  bookmarked,
  followUpV2,
  getConversationIdV2,
  getConversationListV2,
  getFaqV2,
  graphApi,
  startConversationV2,
  unBookmarked,
} from "Samarth Module/api/samarthAiAPI";
import MessagebulbIcon from "../../../assets/images/chatrobot.gif";
import ChatBotIcon from "../../../assets/images/messagebulbs.gif";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  headerContainerStyle,
  headerTitleStyle,
  headerIconsContainerStyle,
  BoxShade,
  greenShade,
  MenuStyles,
  SwitchStyles,
  tooltipProps,
  AskStyle,
  AskOverallStyle,
  ExpandIconStyle,
  BoxStyle,
  DisplayStyle,
} from "./ChatBot.styles";
import DataLake from "Samarth Module/components/SamarthAi/DataLakeAI/DataLake";
import { Retry } from "Samarth Module/components/SamarthAi/Retry";
import PromtsHistoryDrawer from "Samarth Module/components/SamarthAi/PromtsHistoryDrawer";
import ExpandIcon from "../../../assets/images/expandmore.svg";
import ChatFilter from "Samarth Module/components/chatbot/ChatFilter";
import ChattextIcon from "../../../assets/images/chattext.gif";
import ErrorIcon from "../../../assets/images/errorboticon.svg";
import DoubleQuotesIcon from "../../../assets/images/dbquotes.svg";
import moreIcon from "../../../assets/images/chatdots.svg";
import FilledBookmarkIcon from "../../../assets/images/fillBookmarked.svg";
import MyDashboardsList from "../MyDashboard/Dashboard";
import MyDashboard from "../MyDashboard/MyDashboard";
import {
  hasGraphRows,
  parseFiltersFromQuestion,
  buildGraphObject,
  INTRO_REVEAL_DELAYS,
  OUTPUT_OPTIONS,
  CATEGORY_OPTIONS,
  QUICK_OPTIONS,
  truncateWords,
  HeaderIconWithLabel,
  QuotedTextChip,
} from "./chatBotFormatters";
import exportChatAsPdf from "./exportChatAsPdf";

// ── Component ──
export default function ChatBot({ isExpand, onExpand }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("UserInfo"))?.user_info,
    [],
  );

  // ── Core state ──
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedQuickValue, setSelectedQuickValue] = useState("");
  const [selectedOptionValue, setSelectedOptionValue] = useState("");
  const [spaceId, setSpaceId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [active, setActive] = useState("ai");
  const [conversationList, setConversationList] = useState([]);
  const [title, setTitle] = useState("");
  const [faq, setFaq] = useState([]);
  const [pageloading, setPageloading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errors, setErrors] = useState({});
  const [introStep, setIntroStep] = useState(0);
  const [messageId, setMessageId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectionPopup, setSelectionPopup] = useState(null);
  const [quotedText, setQuotedText] = useState("");
  const [isBookmarked, setIsBookmarked] = useState(
    location.state?.is_bookmarked ?? false,
  );
  const [view, setView] = useState(location.state?.view || "menu");
  const [selectedDashboardId, setSelectedDashboardId] = useState(
    location.state?.selectedId || null,
  );
  // ── Refs ──
  const chatContainerRef = useRef(null);
  const introTimersRef = useRef([]);
  const lastPayloadRef = useRef(null);
  const isFetchingConvRef = useRef(false);
  const chatPageRef = useRef(1);
  const hasNextChatRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  const activeConvIdRef = useRef(null);
  const conversationsCacheRef = useRef({
    pages: new Map(),
    hasNext: true,
    lastPage: 0,
  });
  const prevConversationId = useRef(null);
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  // ── Memoized tab config (depends on faq) ──
  const tabs = useMemo(
    () => [{ label: "Frequently Used Prompts", prompts: Object.values(faq) }],
    [faq],
  );

  // ── Stable callbacks ──
  const closeAnchor = useCallback(() => setAnchorEl(null), []);

  const attachFilterInPrompt = useCallback(() => {
    if (!formValues) return "";
    return Object.entries(formValues)
      .filter(([, v]) => v?.length > 0)
      .map(([k, v]) => `${k}=${v.join(",")};`)
      .join(" ");
  }, [formValues]);

  // ── Text selection → "Ask Samarth" floating button ──
  const handleAskSamarth = useCallback(() => {
    if (!selectionPopup?.text) return;
    setQuotedText(selectionPopup.text);
    setSelectionPopup(null);
    window.getSelection()?.removeAllRanges();
  }, [selectionPopup]);
  useEffect(() => {
    if (location.state?.is_bookmarked !== undefined) {
      setIsBookmarked(location.state.is_bookmarked);
    }
  }, [location.state]);
  useEffect(() => {
    const handleSelection = () => {
      const container = chatContainerRef.current;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        setSelectionPopup(null);
        return;
      }
      const text = selection.toString().trim();
      if (!text) {
        setSelectionPopup(null);
        return;
      }
      const anchor = selection.anchorNode;
      if (!container || !anchor || !container.contains(anchor)) {
        setSelectionPopup(null);
        return;
      }
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return;
      const POPUP_WIDTH = 120;
      const POPUP_HEIGHT = 32;
      const top = Math.max(rect.top - POPUP_HEIGHT - 8, 8);
      const left = Math.min(
        Math.max(rect.right - POPUP_WIDTH, 8),
        window.innerWidth - POPUP_WIDTH - 8,
      );
      setSelectionPopup({ top, left, text });
    };

    const handleMouseDown = (e) => {
      if (e.target.closest && e.target.closest("[data-ask-samarth-popup]"))
        return;
      setSelectionPopup(null);
    };

    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  // ── Format conversation messages from API into local shape ──
  const formatConversationMessages = useCallback((apiMessages) => {
    if (!apiMessages?.length) return;

    const formatted = [];
    let lastSpaceId = null;
    let lastMessageId = null;

    for (const msg of apiMessages) {
      if (msg?.space_id) lastSpaceId = msg.space_id;
      if (msg?.message_id) lastMessageId = msg.message_id;

      const graphPayload = msg?.graph_data;
      const graph = graphPayload?.graph_data;
      const graphHasRows = hasGraphRows(graph);
      const statement = msg?.response_attachment?.statement_response;
      const hasData =
        msg?.response_attachment &&
        (Array.isArray(msg.response_attachment)
          ? msg.response_attachment.length > 0
          : Object.keys(msg.response_attachment || {}).length > 0);
      const isSuccess = statement?.status?.state === "SUCCEEDED";
      const shouldShowGraph = hasData && isSuccess;
      const graphFetched = !!graph;
      const noGraphData = graphFetched && !graphHasRows;
      const rowStatus = statement?.manifest?.total_row_count > 0;
      const tableStatus = isSuccess && rowStatus;
      // User message
      if (msg?.question) {
        const {
          text: questionText,
          filters: parsedFilters,
          quotedText: parsedQuotedText,
        } = parseFiltersFromQuestion(msg.question);
        formatted.push({
          id: msg.message_id,
          sender: "user",
          type: "text",
          text: questionText || msg.question,
          filters: parsedFilters,
          quotedText: parsedQuotedText || undefined,
          is_pinned: msg?.is_pinned ?? false,
        });
      }

      // Bot response
      if (msg?.explanation || msg?.response_attachment) {
        const columns = statement?.manifest?.schema?.columns || [];
        const rows = statement?.result?.data_array || [];
        const commonBotProps = {
          id: msg.message_id,
          sender: "bot",
          feedback: msg?.feedback,
          showGraph: shouldShowGraph,
          viewMode: "table",
          graphFetched,
          loadingGraph: false,
          noGraphData,
          tableStatus,
          graph: buildGraphObject(graphPayload, graph),
        };

        if (statement) {
          formatted.push({
            ...commonBotProps,
            type: "table",
            text: msg?.explanation || "",
            columns,
            rows,
          });
        } else {
          formatted.push({
            ...commonBotProps,
            type: "text",
            text:
              msg?.explanation ||
              (typeof msg?.response_attachment === "string"
                ? msg.response_attachment
                : "No response received"),
          });
        }
      }
    }

    if (lastSpaceId) setSpaceId(lastSpaceId);
    if (lastMessageId) setMessageId(lastMessageId);
    setMessages((prev) => [...prev, ...formatted]);
  }, []);

  // ── Append a single bot message (for send/edit flow) ──
  const appendBotMessage = useCallback((data, editId = null) => {
    const queryResult = data?.query_result;
    const statement = queryResult?.statement_response;
    const hasData =
      queryResult &&
      (Array.isArray(queryResult)
        ? queryResult.length > 0
        : typeof queryResult === "object" &&
          Object.keys(queryResult).length > 0);
    const isSuccess = statement?.status?.state === "SUCCEEDED";
    const shouldShowGraph = hasData && isSuccess;
    const graphPayload = data?.graph_data || null;
    const graph = graphPayload?.graph_data;
    const graphFetched = !!graph;
    const graphHasRows = hasGraphRows(graph);
    const noGraphData = graphFetched && !graphHasRows;
    const rowStatus = statement?.manifest?.total_row_count > 0;
    const tableStatus = isSuccess && rowStatus;
    const newBotMessage = statement
      ? {
          id: data.message_id,
          sender: "bot",
          type: "table",
          columns: statement?.manifest?.schema?.columns || [],
          rows: statement?.result?.data_array || [],
          text: data?.explanation || "No response received",
          graphPayload,
          viewMode: "table",
          graph: buildGraphObject(graphPayload, graph),
          graphFetched,
          loadingGraph: false,
          noGraphData,
          tableStatus,
          showGraph: shouldShowGraph,
        }
      : {
          id: data.message_id,
          sender: "bot",
          type: "text",
          text: data?.explanation || "No response received",
        };

    setMessages((prev) => {
      if (editId) {
        const idx = prev.findIndex((m) => m.id === editId);
        if (
          idx !== -1 &&
          idx + 1 < prev.length &&
          prev[idx + 1]?.sender === "bot"
        ) {
          const updated = [...prev];
          updated[idx + 1] = newBotMessage;
          return updated;
        }
      }
      return [...prev, newBotMessage];
    });
  }, []);

  // ── Conversation history cache helpers ──
  const mergeCachedPages = useCallback(() => {
    const cache = conversationsCacheRef.current;
    const merged = [];
    const seen = new Set();
    for (let p = 1; p <= cache.lastPage; p++) {
      const pageData = cache.pages.get(p);
      if (!pageData) continue;
      for (const conv of pageData) {
        if (!seen.has(conv.conversation_id)) {
          seen.add(conv.conversation_id);
          merged.push(conv);
        }
      }
    }
    return merged;
  }, []);

  const hydrateConversationsFromCache = useCallback(() => {
    const cache = conversationsCacheRef.current;
    if (cache.pages.size === 0) return false;
    setConversationList(mergeCachedPages());
    setPage(cache.lastPage);
    setHasNext(cache.hasNext);
    return true;
  }, [mergeCachedPages]);

  // ── Fetch conversation list (with page-wise cache) ──
  const fetchConversations = useCallback(
    async (pageNumber, force = false) => {
      if (!userInfo?.id) return;
      if (!force && !hasNext) return;
      const cache = conversationsCacheRef.current;

      if (!force && cache.pages.has(pageNumber)) {
        const cachedPage = cache.pages.get(pageNumber);
        setConversationList((prev) => {
          const existingIds = new Set(prev.map((c) => c.conversation_id));
          const additions = cachedPage.filter(
            (c) => !existingIds.has(c.conversation_id),
          );
          return additions.length ? [...prev, ...additions] : prev;
        });
        setHasNext(cache.hasNext);
        return;
      }

      try {
        setPageloading(true);
        const data = await getConversationListV2(pageNumber, {
          user_id: userInfo.id,
          user_name: userInfo.name,
          user_email: userInfo.email,
        });
        if (data?.conversation) {
          cache.pages.set(pageNumber, data.conversation);
          cache.lastPage = Math.max(cache.lastPage, pageNumber);
          // Force-fetch page 1 → replace the list (avoid duplicates after refresh).
          // Pagination append (page > 1) → deduplicate against existing IDs.
          if (force && pageNumber === 1) {
            setConversationList(data.conversation);
          } else {
            setConversationList((prev) => {
              const existingIds = new Set(prev.map((c) => c.conversation_id));
              const additions = data.conversation.filter(
                (c) => !existingIds.has(c.conversation_id),
              );
              return additions.length ? [...prev, ...additions] : prev;
            });
          }
        }
        cache.hasNext = data?.has_next ?? false;
        setHasNext(data?.has_next);
      } catch (error) {
        console.error("Error fetching conversations:", error);
      } finally {
        setPageloading(false);
      }
    },
    [userInfo, hasNext],
  );

  // ── Fetch a single conversation by ID ──
  const fetchConversationId = useCallback(
    async (convId, pageNumber, force = false, { showLoader = false } = {}) => {
      if (!userInfo?.id) return;
      if (!force && !hasNextChatRef.current) return;
      if (isFetchingConvRef.current) return;

      isFetchingConvRef.current = true;
      activeConvIdRef.current = convId;

      if (showLoader) setInitialLoading(true);

      try {
        setPageloading(true);

        if (pageNumber === 1) {
          setMessages([]);
          setConversationId(convId);
          chatPageRef.current = 1;
          hasNextChatRef.current = true;
        }

        // Get raw response as text
        const response = await getConversationIdV2(convId, pageNumber, {
          responseType: "text",
        });

        let safeData = null;

        try {
          const rawText = response;

          if (!rawText) {
            console.error("Empty response");
            return;
          }

          // Replace NaN → null
          const cleanedText = rawText.replace(/\bNaN\b/g, "null");

          // Parse safely
          safeData = JSON.parse(cleanedText);
        } catch (e) {
          console.error("JSON parse failed:", e);

          // Fallback: try using original parsed data (if axios partially worked)
          safeData = response;

          if (!safeData) {
            throw new Error("Invalid API response");
          }
        }

        if (activeConvIdRef.current !== convId) return;

        // Use safeData
        if (safeData?.status) {
          formatConversationMessages(safeData.messages);
          setTitle(safeData?.conversation_title);
        }

        const hasMore = safeData?.has_next ?? false;
        hasNextChatRef.current = hasMore;
        chatPageRef.current = pageNumber;
        setPage(pageNumber);

        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[convId];
          return updated;
        });
      } catch (error) {
        if (activeConvIdRef.current !== convId) return;

        console.error("Error fetching conversation:", error);

        setErrors((prev) => ({
          ...prev,
          [convId]: error?.message || "Failed to load conversation.",
        }));
      } finally {
        setPageloading(false);
        isFetchingConvRef.current = false;
        if (showLoader) setInitialLoading(false);
      }
    },
    [userInfo, formatConversationMessages],
  );

  // ── Send / edit message ──
  const handleSendMessage = useCallback(
    async (overrideText = null, editId = null) => {
      const userText = overrideText ?? newMessage;
      if (!userText.trim()) return;
      const filters = attachFilterInPrompt();
      const quotePrefix = quotedText ? `> ${quotedText}\n\n` : "";
      const content = `${quotePrefix}${userText} ${filters}`.trim();

      setLoading(true);
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[conversationId ?? "new"];
        return updated;
      });

      const activeFilters = {};
      if (formValues) {
        for (const key of Object.keys(formValues)) {
          if (formValues[key]?.length > 0) {
            activeFilters[key] = [...formValues[key]];
          }
        }
      }

      try {
        if (editId) {
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === editId);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              text: userText,
              filters: activeFilters,
            };
            return updated.slice(0, idx + 1);
          });
          setEditingMessageId(null);
          setEditedText("");
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              sender: "user",
              text: userText,
              filters: activeFilters,
              quotedText: quotedText || undefined,
            },
          ]);
        }

        if (!overrideText) setNewMessage("");
        if (quotedText) setQuotedText("");

        const basePayload = {
          content,
          user_id: userInfo.id,
          user_name: userInfo.name,
          user_email: userInfo.email,
          ...(editId && { message_id: editId }),
        };

        const isNewChat = !conversationId;
        const payload = !isNewChat
          ? {
              ...basePayload,
              conversation_id: conversationId,
              space_id: spaceId,
            }
          : basePayload;

        lastPayloadRef.current = { payload, editId };
        const { data } = !isNewChat
          ? await followUpV2(payload)
          : await startConversationV2(payload);
        if (data?.conversation_title) setTitle(data.conversation_title);
        // New conversation setup
        if (isNewChat && data?.conversation_id) {
          const newId = data.conversation_id;
          if (newId && !isExpand) {
            navigate(`/samarth/chat/${newId}`, { replace: true });
          }
          setConversationId(newId);
          setPage(1);
          setConversationList([]);
          conversationsCacheRef.current = {
            pages: new Map(),
            hasNext: true,
            lastPage: 0,
          };
          setHasNext(true);
          fetchConversations(1, true);
        }

        if (data?.space_id) setSpaceId(data.space_id);

        if (data?.message_id) {
          const serverId = data.message_id;
          setMessageId(serverId);
          if (!editId) {
            setMessages((prev) => {
              const updated = [...prev];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].sender === "user") {
                  updated[i] = { ...updated[i], id: serverId };
                  break;
                }
              }
              return updated;
            });
          }
        }

        appendBotMessage(data, editId);
        lastPayloadRef.current = null;
      } catch (err) {
        console.error(err);
        setErrors((prev) => ({
          ...prev,
          [conversationId ?? "new"]: err?.message || "Failed to send message.",
        }));
      } finally {
        setLoading(false);
      }
    },
    [
      newMessage,
      conversationId,
      spaceId,
      userInfo,
      appendBotMessage,
      formValues,
      attachFilterInPrompt,
      fetchConversations,
      isExpand,
      navigate,
      quotedText,
    ],
  );

  const handleEditSend = useCallback(
    (msgId) => handleSendMessage(editedText, msgId),
    [editedText, handleSendMessage],
  );

  const handleNewMessageSend = useCallback(() => {
    handleSendMessage();
    setNewMessage("");
    setSelectedQuickValue("");
    setSelectedOptionValue("");
    setFilterOpen(false);
    setQuotedText("");
  }, [handleSendMessage]);

  // ── FAQ ──
  const fetchFaq = useCallback(async () => {
    if (!userInfo?.id || conversationId) return;
    try {
      const data = await getFaqV2({
        user_id: userInfo.id,
        user_name: userInfo.name,
        user_email: userInfo.email,
      });
      if (data?.user_faq) setFaq(data.user_faq);
    } catch (error) {
      console.error("Error fetching FAQ:", error);
    }
  }, [userInfo, conversationId]);
  // ── Graph fetch ──
  const fetchGraphOnly = useCallback(
    async (msgId, override = null) => {
      try {
        let chartChangedFields = {};
        if (override?.chartType) {
          chartChangedFields = {
            is_chart_changed: 1,
            chart_type: override.chartType,
          };
        } else {
          const currentMsg = messagesRef.current.find((m) => m.id === msgId);
          if (currentMsg?.isChartChanged) {
            chartChangedFields = {
              is_chart_changed: 1,
              chart_type: currentMsg.selectedGraphType,
            };
          }
        }

        const payload = {
          conversation_id: conversationId,
          user_id: userInfo.id,
          user_name: userInfo.name,
          user_email: userInfo.email,
          message_id: msgId,
          ...chartChangedFields,
        };

        const response = await graphApi(payload, {
          responseType: "text",
        });

        let safeData = null;

        // STEP 1: Try safe parsing
        try {
          const rawText = response?.data;

          if (typeof rawText === "string") {
            const cleaned = rawText.replace(/\bNaN\b/g, "null");
            safeData = JSON.parse(cleaned);
          } else {
            // already parsed (backend fixed case)
            safeData = rawText;
          }
        } catch (err) {
          console.error("JSON parse failed:", err);

          // STEP 2: Fallback (important)
          safeData = response?.data || null;
        }

        // If still null → don't throw, handle gracefully
        if (!safeData) {
          console.warn("safeData is null");

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === msgId
                ? {
                    ...msg,
                    viewMode: "graph",
                    graphFetched: true,
                    loadingGraph: false,
                    noGraphData: true,
                  }
                : msg,
            ),
          );
          return;
        }

        // ✅ STEP 3: Extract graphPayload safely
        const graphPayload =
          safeData?.graph_data ||
          safeData?.data?.graph_data ||
          safeData?.result?.graph_data;

        if (!graphPayload) {
          console.warn("graphPayload missing");

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === msgId
                ? {
                    ...msg,
                    viewMode: "graph",
                    graphFetched: true,
                    loadingGraph: false,
                    noGraphData: true,
                  }
                : msg,
            ),
          );
          return;
        }

        // ✅ STEP 4: Extract graph
        const graph = graphPayload?.graph_data;

        const noGraphData = !graph || !hasGraphRows(graph);

        // ✅ STEP 5: Build graph safely
        const builtGraph = buildGraphObject(graphPayload, graph);

        // ✅ STEP 6: Update UI
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === msgId
              ? {
                  ...msg,
                  viewMode: "graph",
                  graphFetched: true,
                  loadingGraph: false,
                  noGraphData,
                  graph: builtGraph,
                }
              : msg,
          ),
        );
      } catch (error) {
        console.error("fetchGraphOnly error:", error);

        // ✅ Final fallback (never break UI)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === msgId
              ? {
                  ...msg,
                  viewMode: "graph",
                  graphFetched: true,
                  loadingGraph: false,
                  noGraphData: true,
                }
              : msg,
          ),
        );
      }
    },
    [conversationId, userInfo],
  );
    const handleGraphTypeChange = useCallback(
      (msgId, newType) => {
        if (!msgId || !newType) return;

        const currentMsg = messagesRef.current.find((m) => m.id === msgId);
        if (
          currentMsg?.selectedGraphType === newType &&
          currentMsg?.isChartChanged
        ) {
          return;
        }

        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  isChartChanged: true,
                  selectedGraphType: newType,
                  loadingGraph: true,
                  noGraphData: false,
                  graph: m.graph ? { ...m.graph, type: newType } : m.graph,
                }
              : m,
          ),
        );

        fetchGraphOnly(msgId, { chartType: newType });
      },
      [fetchGraphOnly],
    );
  const handleGraphClick = useCallback(
    (msgId) => {
      let shouldFetch = false;
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== msgId) return msg;
          if (msg.graphFetched || msg.graph) {
            return { ...msg, viewMode: "graph" };
          }
          shouldFetch = true;
          return { ...msg, loadingGraph: true };
        }),
      );
      if (shouldFetch) fetchGraphOnly(msgId);
    },
    [fetchGraphOnly],
  );

  // ── Clipboard ──
  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  const handleCopyTable = useCallback(async (columns, rows) => {
    try {
      const header = "| " + columns.map((col) => col.name).join(" | ") + " |";
      const separator =
        "| " + columns.map(() => "----------").join(" | ") + " |";
      const body = rows
        .map(
          (row) =>
            "| " +
            row
              .map((cell) => (isNaN(cell) ? cell : Number(cell).toFixed(2)))
              .join(" | ") +
            " |",
        )
        .join("\n");
      await navigator.clipboard.writeText(`${header}\n${separator}\n${body}`);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  }, []);

  // ── Prompt click ──
  const handlePromptClick = useCallback((prompt) => setNewMessage(prompt), []);

  // ── New chat ──
  const handleNewChat = useCallback(() => {
    setMessages([]);
    setNewMessage("");
    setSelectedQuickValue("");
    setSelectedOptionValue("");
    setConversationId(null);
    setEditingMessageId(null);
    setEditedText("");
    setTitle("");
    // setView("menu");
    setSelectedDashboardId(null);
    localStorage.removeItem("convId");
    if (!isExpand) navigate("/samarth/chat", { replace: true });
    setFormValues({});
    setFilterOpen(false);
    chatContainerRef.current?.scrollTo(0, 0);
    setQuotedText("");
    setSpaceId(null);
  }, [isExpand, navigate]);

  // ── Scroll handler (pagination on scroll-to-bottom) ──
  const handleScroll = useCallback(
    (e) => {
      if (isProgrammaticScrollRef.current) return;
      if (initialLoading || isFetchingConvRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      if (
        distanceFromBottom < 80 &&
        hasNextChatRef.current &&
        conversationId &&
        chatPageRef.current >= 1
      ) {
        const nextPage = chatPageRef.current + 1;
        setIsLoadingMore(true);
        fetchConversationId(conversationId, nextPage).finally(() =>
          setIsLoadingMore(false),
        );
      }
    },
    [initialLoading, conversationId, fetchConversationId],
  );

  // ── Load more for history sidebar ──
  const loadMore = useCallback(async () => {
    if (!hasNext || pageloading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchConversations(nextPage);
  }, [hasNext, pageloading, page, fetchConversations]);

  // ── History toggle ──
  const handleHistoryToggle = useCallback(async () => {
    setShowHistory((prev) => !prev);
    const hydrated = hydrateConversationsFromCache();
    if (!hydrated) {
      setPage(1);
      setHasNext(true);
      setConversationList([]);
      await fetchConversations(1, true);
    }
  }, [hydrateConversationsFromCache, fetchConversations]);

  // ── Stable handler refs for child callbacks ──
  const handleQuickChange = useCallback(
    (e) => setSelectedQuickValue(e.target.value),
    [],
  );
  const handleOptionChange = useCallback(
    (e) => setSelectedOptionValue(e.target.value),
    [],
  );
  const handleHistoryClose = useCallback(() => setShowHistory(false), []);

  const handleExportPdf = useCallback(() => {
    closeAnchor();
    exportChatAsPdf(chatContainerRef.current, title, messages);
  }, [closeAnchor, title, messages]);

  const handleToggleBookmark = async () => {
    const payload = {
      user_id: userInfo.id,
      user_name: userInfo.name,
      user_email: userInfo.email,
      conversation_id: id,
    };

    try {
      if (isBookmarked) {
        await unBookmarked(payload);
        setIsBookmarked(false);
      } else {
        await bookmarked(payload);
        setIsBookmarked(true);
      }
    } catch (error) {
      console.error("Bookmark action failed", error);
    }
  };
  const menuItems = useMemo(
    () => [
      {
        label: "Export as PDF",
        icon: PdfIcon,
        onClick: handleExportPdf,
      },
      {
        label: isBookmarked ? "Remove Bookmark" : "Bookmark Chat",
        icon: isBookmarked ? FilledBookmarkIcon : BookmarkIcon,
        onClick: handleToggleBookmark,
      },
    ],
    [handleExportPdf, isBookmarked],
  );

  const handleRetry = useCallback(() => {
    if (lastPayloadRef.current) {
      handleSendMessage(
        lastPayloadRef.current.payload.content,
        lastPayloadRef.current.editId,
      );
    }
  }, [handleSendMessage]);

  // ── Effects ──

  // Reset on navigation state
  useEffect(() => {
    if (location.pathname === "/samarth/chat") {
      setMessages([]);
      setTitle("");
      setQuotedText("");
      localStorage.removeItem("convId");
      window.history.replaceState({}, document.title);
    }
  }, [location.key, location.pathname]);

  // Initial conversation load
  const pathname = location.pathname.replace(/\/$/, "");
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  const pathId = /^\d/.test(lastSegment) ? lastSegment : null;

  useEffect(() => {
    const conversationId = id || location.state?.conversationId || pathId;
    if (!conversationId) {
      setInitialLoading(false);
      return;
    }

    setConversationId(conversationId);

    fetchConversationId(conversationId, 1, true, {
      showLoader: true,
    });
  }, [id, location.state?.conversationId, pathId]);
  useEffect(() => {
    // skip first assignment (new chat creation)
    if (
      prevConversationId.current &&
      prevConversationId.current !== conversationId
    ) {
      setFormValues({});
    }

    prevConversationId.current = conversationId;
  }, [conversationId]);
  // FAQ
  useEffect(() => {
    fetchFaq();
  }, [fetchFaq]);

  // Pre-fill message from location state
  useEffect(() => {
    setNewMessage(location.state?.message || "");
  }, [location.state?.message]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (!chatContainerRef.current || isLoadingMore || initialLoading) return;

    const el = chatContainerRef.current;
    let releaseTimer = null;

    const timer = setTimeout(() => {
      isProgrammaticScrollRef.current = true;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });

      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        isProgrammaticScrollRef.current = false;
        el.removeEventListener("scrollend", release);
      };
      if ("onscrollend" in window) {
        el.addEventListener("scrollend", release, { once: true });
        releaseTimer = setTimeout(release, 800);
      } else {
        releaseTimer = setTimeout(release, 600);
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      if (releaseTimer) clearTimeout(releaseTimer);
    };
  }, [messages.length, isLoadingMore, initialLoading]);

  // Intro animation
  useEffect(() => {
    introTimersRef.current.forEach(clearTimeout);
    introTimersRef.current = [];

    if (messages.length > 0) {
      setIntroStep(4);
      return;
    }
    setIntroStep(0);
    INTRO_REVEAL_DELAYS.forEach((delay, index) => {
      const timer = setTimeout(() => setIntroStep(index + 1), delay);
      introTimersRef.current.push(timer);
    });
    return () => {
      introTimersRef.current.forEach(clearTimeout);
      introTimersRef.current = [];
    };
  }, [messages.length]);

  // ── Memoized props objects ──
  const chatInputProps = useMemo(
    () => ({
      messages,
      newMessage,
      setNewMessage,
      onSend: handleNewMessageSend,
      selectedQuickValue,
      handleQuickChange,
      selectedOptionValue,
      handleOptionChange,
      quick_options: QUICK_OPTIONS,
      output_options: OUTPUT_OPTIONS,
      filterOpen,
      setFilterOpen,
      formValues,
      setFormValues,
      selectedCategories,
      setSelectedCategories,
      categoryOptions: CATEGORY_OPTIONS,
    }),
    [
      messages,
      newMessage,
      handleNewMessageSend,
      selectedQuickValue,
      handleQuickChange,
      selectedOptionValue,
      handleOptionChange,
      filterOpen,
      formValues,
      selectedCategories,
    ],
  );
  const handleMyDashboardClick = () => {
    setView("dashboards");
    onExpand?.();
    setSelectedDashboardId(null);
  };
  // ── Render ──
  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "calc(100vh - 52px)",
          overflowX: "hidden",
        }}
      >
        {view === "dashboards" && (
          <Box
            sx={{
              width: "50%",
              transition: "all 0.3s ease",
              borderRight: "1px solid #DFDFDF",
            }}
          >
            {view === "dashboards" && selectedDashboardId ? (
              <MyDashboard
                view={view}
                dashboardId={selectedDashboardId}
                onDashboardSelect={(id) => setSelectedDashboardId(id)}
                conversationId={conversationId}
              />
            ) : (
              <MyDashboardsList
                view={view}
                onDashboardSelect={(id) => setSelectedDashboardId(id)}
                conversationId={conversationId}
              />
            )}
          </Box>
        )}

        <Box
          sx={{
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            width: view === "dashboards" ? "50%" : "100%",
            transition: "all 0.3s ease",
          }}
        >
          {selectionPopup && (
            <Box
              data-ask-samarth-popup
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleAskSamarth}
              sx={{
                position: "fixed",
                top: selectionPopup.top,
                left: selectionPopup.left,
                ...AskOverallStyle,
              }}
            >
              <Box
                component="img"
                src={DoubleQuotesIcon}
                sx={{ width: 14, height: 14, flexShrink: 0 }}
              />
              <Typography sx={{ ...AskStyle }}>Ask Samarth</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: showHistory
                  ? {
                      xs: "calc(100% - 280px)",
                      md: "calc(100% - 324px)",
                      lg: "calc(100% - 432px)",
                    }
                  : "100%",
                minWidth: 0,
                minHeight: 0,
                transition: "flex-basis 0.3s ease",
                overflow: "auto",
              }}
            >
              {/* ── Header ── */}
              <Box sx={headerContainerStyle}>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      backgroundColor:
                        view === "dashboards" || isExpand
                          ? "transparemt"
                          : "#F8FBFC",
                      borderRadius: "12px",
                      padding: "4px",
                      gap: "4px",
                      border:
                        view === "dashboards" || isExpand
                          ? "none"
                          : "1px solid #D9E1E7",
                    }}
                  >
                    {view === "dashboards" || isExpand ? (
                      <>
                        <Box
                          component="img"
                          src={ExpandIcon}
                          sx={{ ...ExpandIconStyle }}
                          onClick={() => {
                            setView("menu");
                            const convId =
                              conversationId || localStorage.getItem("convId");

                            const isValidConvId =
                              convId &&
                              convId !== "chat" &&
                              convId !== "undefined" &&
                              convId !== "null";
                            navigate(
                              isValidConvId
                                ? `/samarth/chat/${conversationId}`
                                : "/samarth/chat",
                              {
                                state: {
                                  conversationId: conversationId,
                                  is_bookmarked: isBookmarked,
                                },
                                replace: true,
                              },
                            );
                          }}
                        />
                      </>
                    ) : (
                      !isExpand && (
                        <Box
                          onClick={() => setActive("ai")}
                          sx={{
                            ...SwitchStyles,
                            backgroundColor:
                              active === "ai" ? "#FFFFFF" : "transparent",
                            boxShadow:
                              active === "ai"
                                ? "0px 1px 3px rgba(0,0,0,0.15)"
                                : "none",
                          }}
                        >
                          <Typography
                            className="SwitchOptions"
                            sx={{
                              color: active === "ai" ? "#1A2025" : "#4D5F6E",
                            }}
                          >
                            Samarth AI
                          </Typography>
                        </Box>
                      )
                    )}

                    {/* <Box
                  onClick={() => setActive("lake")}
                  sx={{
                    ...SwitchStyles,
                    backgroundColor:
                      active === "lake" ? "#FFFFFF" : "transparent",
                    boxShadow:
                      active === "lake"
                        ? "0px 1px 3px rgba(0,0,0,0.15)"
                        : "none",
                  }}
                >
                  <Typography
                    className="SwitchOptions"
                    sx={{ color: active === "lake" ? "#1A2025" : "#4D5F6E" }}
                  >
                    Samarth Data Lake
                  </Typography>
                </Box> */}
                  </Box>
                  <Tooltip title={title} placement="top" {...tooltipProps}>
                    <Typography sx={{ ...headerTitleStyle, marginLeft: "8px" }}>
                      {truncateWords(
                        title,
                        view === "dashboards" || isExpand ? 3 : 5,
                      )}
                    </Typography>
                  </Tooltip>
                </Box>

                {!showHistory && (
                  <Box sx={headerIconsContainerStyle}>
                    {conversationId && (
                      <HeaderIconWithLabel
                        label="More"
                        icon={moreIcon}
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        labelClassName="morelable"
                        labelMr={1}
                      />
                    )}
                    <HeaderIconWithLabel
                      label="New Chat"
                      icon={NoteIcon}
                      onClick={handleNewChat}
                      labelClassName="newChatLabel"
                      labelMr={conversationId ? 7 : 1}
                    />
                    <HeaderIconWithLabel
                      label="Prompts & History"
                      icon={SliderIcon}
                      onClick={handleHistoryToggle}
                      labelClassName="historyLabel"
                      labelMr={conversationId ? 12 : 7}
                    />
                  </Box>
                )}
              </Box>

              {/* ── Chat messages ── */}
              <Box
                ref={chatContainerRef}
                onScroll={handleScroll}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  minHeight: 0,
                  scrollbarWidth: "thin",
                  scrollbarColor: "#D9E1E7 transparent",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#D9E1E7",
                    borderRadius: 8,
                  },
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  sx={{
                    minHeight: "100%",
                    px: 2,
                    py: 2,
                    maxWidth: isExpand
                      ? { xs: "100%", sm: "90%", md: "768px", lg: "768px" }
                      : { xs: "100%", sm: "90%", md: "100%", lg: "1117px" },
                    width: "100%",
                    m: "0 auto",
                  }}
                >
                  {initialLoading && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flex: 1,
                        minHeight: 200,
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: "#0066CC" }} />
                    </Box>
                  )}

                  {!initialLoading &&
                    active !== "lake" &&
                    messages.map((msg, idx) => (
                      <MessageBubble
                        key={`${msg.id}-${idx}`}
                        msg={msg}
                        editingMessageId={editingMessageId}
                        editedText={editedText}
                        setEditedText={setEditedText}
                        setEditingMessageId={setEditingMessageId}
                        onEditSend={handleEditSend}
                        onCopy={handleCopy}
                        onCopyTable={handleCopyTable}
                        onRefresh={handleGraphClick}
                        conversationId={conversationId}
                        spaceId={spaceId}
                        messageId={msg.id}
                        lastId={messageId}
                        onGraphClick={() => handleGraphClick(msg.id)}
                        onGraphTypeChange={handleGraphTypeChange}
                        formValues={formValues}
                      />
                    ))}

                  {!initialLoading &&
                    !loading &&
                    errors[conversationId ?? "new"] && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        // alignItems="center"
                        // justifyContent="center"
                        gap="12px"
                      >
                        <Box
                          component="img"
                          src={ErrorIcon}
                          width={80}
                          height={80}
                        />

                        <Retry
                          message="Oops! That didn't work. Let's try again."
                          onRetry={handleRetry}
                        />
                      </Box>
                    )}

                  {!initialLoading && loading && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "end",
                      }}
                    >
                      <Box
                        component="img"
                        src={MessagebulbIcon}
                        width={80}
                        height={80}
                      />
                      <Box
                        component="img"
                        src={ChatBotIcon}
                        width={20}
                        height={20}
                        style={{ marginBottom: "10px" }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* ── Bottom bar (input + intro) ── */}
              {!initialLoading && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    mb: 2,
                  }}
                >
                  {messages.length === 0 ? (
                    <Box
                      textAlign="center"
                      sx={{
                        maxWidth: { md: "100%", lg: "1177px" },
                        width: "100%",
                        m: "0 auto",
                        px: { xs: 2, lg: 2 },
                        position: "relative",
                      }}
                    >
                      <Box
                        component="img"
                        src={LinesImg}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          opacity: introStep >= 1 ? 1 : 0,
                          transition: "opacity 0.45s ease",
                          pointerEvents: "none",
                        }}
                      />
                      <Box sx={{ ...BoxStyle }}>
                        <Box
                          sx={{
                            minHeight: { xs: 100, md: 120, lg: 160 },
                            ...DisplayStyle,
                          }}
                        >
                          <Box
                            component="img"
                            src={BotIcon}
                            sx={{
                              maxWidth: { xs: 100, md: 120, lg: 160 },
                              position: "relative",
                              borderRadius: "8px",
                              opacity: introStep >= 1 ? 1 : 0,
                              visibility: introStep >= 1 ? "visible" : "hidden",
                              animation:
                                introStep >= 1
                                  ? "introFadeIn 0.45s ease both"
                                  : "none",
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            minHeight: { xs: 36, lg: 44 },
                            ...DisplayStyle,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#000000",
                              fontSize: { xs: "14px", md: "16px", lg: "24px" },
                              position: "relative",
                              opacity: introStep >= 2 ? 1 : 0,
                              visibility: introStep >= 2 ? "visible" : "hidden",
                              animation:
                                introStep >= 2
                                  ? "introFadeIn 0.45s ease both"
                                  : "none",
                            }}
                          >
                            {active === "ai"
                              ? "I am Samarth AI"
                              : "Samarth Data Lake"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            minHeight: { xs: 48, lg: 64 },
                            ...DisplayStyle,
                          }}
                        >
                          <Box
                            component="img"
                            src={ChattextIcon}
                            alt="Info"
                            sx={{
                              width: "40%",
                              mb: { md: 1, lg: 2 },
                              opacity: introStep >= 3 ? 1 : 0,
                              visibility: introStep >= 3 ? "visible" : "hidden",
                              animation:
                                introStep >= 3
                                  ? "introTextReveal 0.6s ease both"
                                  : "none",
                              "@media (max-width:1024px)": { width: "25%" },
                              "@media (min-width:1025px) and (max-width:1270px)":
                                {
                                  width: "30%",
                                },
                              "@media (min-width:1271px)": { width: "36%" },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          ...BoxShade,
                          background:
                            introStep >= 4
                              ? "linear-gradient(180deg, rgba(219, 225, 230, 0.2) 0%, rgba(166, 181, 191, 0.2) 100%)"
                              : "",
                        }}
                      >
                        <Box sx={{ borderRadius: "32px", overflow: "hidden" }}>
                          <Box sx={greenShade(introStep)}>
                            {active === "ai" && (
                              <>
                                {filterOpen && (
                                  <ChatFilter
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                  />
                                )}
                                <Box sx={{ position: "relative" }}>
                                  {quotedText && (
                                    <QuotedTextChip
                                      text={quotedText}
                                      onClose={() => setQuotedText("")}
                                    />
                                  )}
                                  <ChatInput {...chatInputProps} />
                                  {tabs[0].prompts.length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 2,
                                        minHeight: { xs: 180, lg: 200 },
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          position: "relative",
                                          zIndex: 1,
                                          visibility:
                                            introStep >= 4
                                              ? "visible"
                                              : "hidden",
                                          clipPath:
                                            introStep >= 4
                                              ? "inset(0 0 0 0)"
                                              : "inset(0 0 100% 0)",
                                          transform:
                                            introStep >= 4
                                              ? "translateY(0)"
                                              : "translateY(-12px)",
                                          transition:
                                            "clip-path 0.6s ease, transform 0.6s ease",
                                        }}
                                      >
                                        <Prompts
                                          tabs={tabs}
                                          onPromptClick={handlePromptClick}
                                          view={isExpand ? "dashboards" : view}
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </>
                            )}
                            {active === "lake" && (
                              <Box sx={{ position: "relative", zIndex: 10 }}>
                                <DataLake />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      textAlign="center"
                      sx={{
                        maxWidth: { md: "100%", lg: "1177px" },
                        width: "100%",
                        m: "0 auto",
                        px: { xs: 2, lg: 2 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "end",
                      }}
                    >
                      <Box
                        sx={{
                          ...BoxShade,
                          background:
                            "linear-gradient(180deg, rgba(219, 225, 230, 0.2) 0%, rgba(166, 181, 191, 0.2) 100%)",
                        }}
                      >
                        <Box sx={greenShade(introStep)}>
                          {filterOpen && (
                            <ChatFilter
                              formValues={formValues}
                              setFormValues={setFormValues}
                              onClose={() => setFilterOpen(false)}
                            />
                          )}
                          {active === "ai" && quotedText && (
                            <QuotedTextChip
                              text={quotedText}
                              onClose={() => setQuotedText("")}
                            />
                          )}
                          <Box
                            sx={{ borderRadius: "32px", overflow: "hidden" }}
                          >
                            {active === "ai" && (
                              <ChatInput {...chatInputProps} />
                            )}
                            {active === "lake" && (
                              <Box sx={{ position: "relative", zIndex: 10 }}>
                                <DataLake />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* ── History sidebar ── */}
            {view === "dashboards" || isExpand ? (
              <PromtsHistoryDrawer
                open={showHistory}
                onClose={handleHistoryClose}
                conversationList={conversationList}
                setConversationList={setConversationList}
                loadMore={loadMore}
                fetchConversationId={fetchConversationId}
                handleNewChat={handleNewChat}
                fetchConversations={fetchConversations}
                onMyDashboardClick={handleMyDashboardClick}
                setConversationId={setConversationId}
                setIsBookmarked={setIsBookmarked}
                currentPage={page}
              />
            ) : (
              <PromtsHistory
                open={showHistory}
                onClose={handleHistoryClose}
                conversationList={conversationList}
                setConversationList={setConversationList}
                loadMore={loadMore}
                fetchConversationId={fetchConversationId}
                handleNewChat={handleNewChat}
                fetchConversations={fetchConversations}
                onMyDashboardClick={handleMyDashboardClick}
                setConversationId={setConversationId}
                currentPage={page}
              />
            )}
            <ActionMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeAnchor}
              menuItems={menuItems}
              menuSx={MenuStyles}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}
